import { VisualSettings } from '@/components/canvas/types/visualSettings'

export type LabelSettings = Pick<
  NonNullable<VisualSettings['table']>,
  'showColHeaders' | 'showRowHeaders'
>

const LABEL_KEYS = ['showColHeaders', 'showRowHeaders'] as const

function labelSettingsToKeep(current?: VisualSettings | null): LabelSettings | null {
  const labels: LabelSettings = {}
  LABEL_KEYS.forEach(key => {
    const value = current?.table?.[key]
    if (value !== undefined) {
      labels[key] = value
    }
  })
  return Object.keys(labels).length > 0 ? labels : null
}

/**
 * Visual settings to save after a shape's data selection is created or updated.
 * Settings that belong to the old selection (its number format, conditional formatting,
 * indexing, ...) are reset, even when the new selection has no consistent number format.
 * Row/column label visibility is a display choice and survives a re-map (issue #2466), as
 * do the sig-test display settings of an updated selection (issue #554).
 * @param current the shape's visual settings before the re-map
 * @param numberFormat the number format of the new selection, if consistent
 * @param isUpdate whether an existing data selection was updated (vs created)
 * @returns the complete visual settings to store (`null` clears them)
 */
export function visualSettingsAfterRemap(
  current: VisualSettings | null | undefined,
  numberFormat: string | null | undefined,
  { isUpdate }: { isUpdate: boolean }
): VisualSettings | null {
  const labels = labelSettingsToKeep(current)
  const kept = {
    ...(numberFormat ? { numberFormat } : {}),
    ...(labels && { table: labels })
  }
  if (isUpdate) {
    // An empty object is what an update has always stored when no sig test was set up.
    const significanceTest = current?.significanceTest || ({} as VisualSettings['significanceTest'])
    return { ...kept, significanceTest }
  }
  return Object.keys(kept).length > 0 ? kept : null
}
